import { tabsAnatomy } from '@ark-ui/react';
import { createSystem, defaultConfig, defineSlotRecipe, defineRecipe } from '@chakra-ui/react';
import { iconRecipe as chakraIconRecipe } from '@chakra-ui/react/theme';
import { nativeSelectAnatomy, selectAnatomy, fieldAnatomy, cardAnatomy } from '@chakra-ui/react/anatomy';

export const fieldSlotRecipe = defineSlotRecipe({
    slots: fieldAnatomy.keys(),
    base: {
        label: { color: { base: 'musa.light.label', _dark: 'musa.dark.label' }, fontSize: '12px' },
    },
});

export const selectSlotRecipe = defineSlotRecipe({
    slots: selectAnatomy.keys(),
    base: {
        root: {
            bg: { base: '#FAFAFA', _dark: '#454050' },
        },
        trigger: {},
        indicatorGroup: {},
        indicator: {},
        content: {},
        item: {},
        control: {},
        itemText: {},
        itemGroup: {},
        itemGroupLabel: {},
        label: {},
        valueText: {},
    },
    variants: {
        variant: {
            outline: {},
            subtle: {},
        },
        size: {
            sm: {},
        },
    },
    defaultVariants: {
        size: 'sm',
        variant: 'outline',
    },
});

const cardSlotRecipe = defineSlotRecipe({
    className: 'chakra-card',
    slots: cardAnatomy.keys(),
    variants: {
        size: {
            md: {
                root: {
                    '--card-padding': '16px',
                },
            },
        },
        variant: {
            elevated: {
                root: {
                    bg: { base: 'white', _dark: 'musa.dark.500' },
                    borderColor: { base: 'musa.light.border', _dark: 'musa.dark.border' },
                },
            },
            outline: {
                root: {
                    bg: { base: 'white', _dark: 'musa.dark.500' },
                    borderColor: { base: 'musa.light.border', _dark: 'musa.dark.border' },
                },
            },
            subtle: {},
        },
    },
});

export const nativeSelectSlotRecipe = defineSlotRecipe({
    className: 'chakra-native-select',
    slots: nativeSelectAnatomy.keys(),
    base: {
        root: {},
        field: {},
        indicator: {},
    },
    variants: {
        variant: {
            outline: {
                field: {
                    border: '1px solid rgba(0, 0, 0, 0.16)',
                    borderRadius: '4px',
                    height: '36px',
                    fontSize: '12px',
                    _placeholder: { color: 'gray.400' },
                    borderColor: { base: '#D6D6D6', _dark: '#64606c' },
                    background: { base: 'rgba(0, 0, 0, 0.02)', _dark: '#454050' },
                },
            },
            subtle: {},
            plain: {},
        },
    },
    defaultVariants: selectSlotRecipe.defaultVariants,
});

export const inputRecipe = defineRecipe({
    base: {},
    variants: {
        variant: {
            outline: {
                border: '1px solid rgba(0, 0, 0, 0.16)',
                borderRadius: '4px',
                height: '36px',
                fontSize: '12px',
                _placeholder: { color: 'gray.400' },
                borderColor: { base: '#D6D6D6', _dark: '#64606c' },
                bg: { base: 'rgba(0, 0, 0, 0.02)', _dark: '#454050' },
            },
        },
    },
    defaultVariants: {
        variant: 'outline',
    },
});

export const textRecipe = defineRecipe({
    base: {
        color: { base: '#2A2339', _dark: 'rgba(255, 255, 255, 0.92)' },
        fontSize: '14px',
    },
    variants: {
        variant: {
            'section-heading': {
                fontWeight: 500,
                fontSize: 'xl',
                marginBottom: '16px',
            },
            subtle: {
                color: { base: '#6A6574', _dark: 'rgba(255, 255, 255, 0.92)' },
            },
        },
    },
});

export const textareaRecipe = defineRecipe({
    base: {},
    variants: {
        variant: {
            outline: {
                bg: { base: '#FAFAFA', _dark: '#454050' },
            },
        },
    },
    defaultVariants: {
        variant: 'outline',
    },
});

export const tabSlotRecipe = defineSlotRecipe({
    slots: tabsAnatomy.keys(),
    variants: {
        variant: {
            line: {
                trigger: {
                    _selected: {
                        color: 'purple.500',
                        borderBottom: '2px solid',
                        borderColor: 'purple.500',
                    },
                },
            },
            enclosed: {
                list: {
                    backgroundColor: '#EAE9EB',
                },
                trigger: {
                    _selected: {
                        color: '#2A2339',
                        fontWeight: 'medium',
                    },
                },
            },
        },
    },
    defaultVariants: {
        variant: 'line',
    },
});

export const iconRecipe = defineRecipe({
    ...chakraIconRecipe,
    variants: {
        size: {
            ...chakraIconRecipe.variants?.size,
            inherit: {
                boxSize: '1em',
            },
        },
    },
});

export const system = createSystem(defaultConfig, {
    theme: {
        recipes: {
            input: inputRecipe,
            text: textRecipe,
            textarea: textareaRecipe,
            icon: iconRecipe,
        },
        slotRecipes: {
            field: fieldSlotRecipe,
            select: selectSlotRecipe,
            nativeSelect: nativeSelectSlotRecipe,
            card: cardSlotRecipe,
            tabs: tabSlotRecipe,
        },
        tokens: {
            fonts: {
                heading: { value: `'Inter Variable', sans-serif` },
                body: { value: `'Inter Variable', sans-serif` },
            },
            colors: {
                purple: {
                    50: { value: '#FAF5FF' },
                    100: { value: '#E9D8FD' },
                    200: { value: '#D6BCFA' },
                    300: { value: '#B794F4' },
                    400: { value: '#9F7AEA' },
                    500: { value: '#805AD5' },
                    600: { value: '#6B46C1' },
                    700: { value: '#553C9A' },
                    800: { value: '#44337A' },
                    900: { value: '#322659' },
                },
                red: {
                    100: { value: '#FED7D7' },
                    200: { value: '#E53E3E' },
                    700: { value: '#E53E3E' },
                },
                musa: {
                    light: {
                        border: { value: '#E1E0E3' },
                        label: { value: '#6A6574' },
                        100: { value: '#2A2339' },
                        500: { value: '#F1F1F2' },
                    },
                    dark: {
                        label: { value: 'rgba(255, 255, 255, 0.64)' },
                        border: { value: '#5D5968' },
                        100: { value: '#221D2E' },
                        200: { value: '#352E43' },
                        300: { value: '#3E3949' },
                        400: { value: '#554F61' },
                        500: { value: '#3f394D' },
                    },
                    offWhite: { value: '#f6f6f7' },
                },
            },
        },
    },
});
